import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Components } from '../styled';
import { ShortNote } from '../../../../../api/homeShortsFeeds/types';
import { formatDuration, isNewShort, ShortRow, worthColor } from '../utils';

export type AddPlacement = { type: 'top' } | { type: 'bottom' } | { type: 'position'; position: number };

interface Props {
  open: boolean;
  feedName: string;
  /** shorts NOT already in the selected feed */
  shorts: ShortRow[];
  /** active shorts that are in no feed at all (including default) */
  unassignedIds: Set<string>;
  /** admin-only editorial notes keyed by short id */
  notes?: Record<string, ShortNote>;
  feedLength: number;
  /** open with the "in no feed" filter on (used by the banner's Review button) */
  initialOnlyUnassigned: boolean;
  onClose: () => void;
  onAdd: (ids: string[], placement: AddPlacement) => void;
}

// Drawer listing shorts that are not in the selected feed, with bulk add at a chosen rank.
const AddShortsPanel = ({
  open,
  feedName,
  shorts,
  unassignedIds,
  notes = {},
  feedLength,
  initialOnlyUnassigned,
  onClose,
  onAdd,
}: Props) => {
  const [search, setSearch] = useState('');
  const [authorFilter, setAuthorFilter] = useState('');
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [position, setPosition] = useState('1');

  useEffect(() => {
    if (open) {
      setSearch('');
      setAuthorFilter('');
      setOnlyUnassigned(initialOnlyUnassigned);
      setSelected(new Set());
      setPosition('1');
    }
  }, [open, initialOnlyUnassigned]);

  const mentors = useMemo(() => {
    const byId = new Map(shorts.map((short) => [short.authorId, short.authorName]));
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [shorts]);

  // Newest first, so fresh uploads are easy to find.
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return shorts
      .filter(
        (short) =>
          (!authorFilter || short.authorId === authorFilter) &&
          (!onlyUnassigned || unassignedIds.has(short.id)) &&
          (!term || `${short.title} ${short.authorName} ${short.id}`.toLowerCase().includes(term)),
      )
      .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
  }, [shorts, search, authorFilter, onlyUnassigned, unassignedIds]);

  const selectedVisible = visible.filter((short) => selected.has(short.id));
  const allVisibleSelected = visible.length > 0 && selectedVisible.length === visible.length;

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const toggleAllVisible = () =>
    setSelected((current) => {
      const next = new Set(current);
      visible.forEach((short) => (allVisibleSelected ? next.delete(short.id) : next.add(short.id)));
      return next;
    });

  const add = (placement: AddPlacement) => {
    onAdd(
      visible.filter((short) => selected.has(short.id)).map((short) => short.id),
      placement,
    );
    onClose();
  };

  const positionNumber = Math.min(Math.max(parseInt(position) || 1, 1), feedLength + 1);
  const hasSelection = selectedVisible.length > 0;

  return (
    <Drawer anchor='right' open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant='h6' component='h2'>
            Add shorts
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            To: {feedName}
          </Typography>
        </Box>
        <IconButton aria-label='Close add shorts panel' onClick={onClose}>
          ✕
        </IconButton>
      </Box>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          size='small'
          label='Search title, mentor or ID'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FormControl size='small'>
          <InputLabel id='add-mentor-filter-label'>Mentor</InputLabel>
          <Select
            labelId='add-mentor-filter-label'
            label='Mentor'
            value={authorFilter}
            onChange={(e) => setAuthorFilter(e.target.value)}>
            <MenuItem value=''>All mentors</MenuItem>
            {mentors.map((mentor) => (
              <MenuItem key={mentor.id} value={mentor.id}>
                {mentor.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Checkbox checked={onlyUnassigned} onChange={(e) => setOnlyUnassigned(e.target.checked)} />}
          label={`Only active shorts in no feed (${unassignedIds.size})`}
        />
      </Box>

      <Box sx={{ px: 2, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={allVisibleSelected}
              indeterminate={hasSelection && !allVisibleSelected}
              onChange={toggleAllVisible}
              disabled={visible.length === 0}
            />
          }
          label={`Select all shown (${visible.length})`}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {visible.length === 0 ? (
          <Components.Empty>No shorts to add with these filters.</Components.Empty>
        ) : (
          <Components.List>
            {visible.map((short) => (
              <Components.Row key={short.id} isDimmed={!short.isActive}>
                <Checkbox
                  checked={selected.has(short.id)}
                  onChange={() => toggle(short.id)}
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  inputProps={{ 'aria-label': `Select ${short.title}` }}
                />
                <Components.RowBody>
                  <Components.RowTitle title={short.title}>{short.title}</Components.RowTitle>
                  <Components.Meta>
                    {short.authorName || 'Unknown mentor'} · {formatDuration(short.durationSeconds)}
                    {!short.isActive ? ' · Inactive' : ''}
                  </Components.Meta>
                </Components.RowBody>
                {notes[short.id]?.worth != null && (
                  <Chip
                    size='small'
                    color={worthColor(notes[short.id].worth as number)}
                    label={`Worth ${notes[short.id].worth}`}
                  />
                )}
                {isNewShort(short) && <Chip size='small' color='info' label='New' />}
                {unassignedIds.has(short.id) && <Chip size='small' variant='outlined' label='In no feed' />}
              </Components.Row>
            ))}
          </Components.List>
        )}
      </Box>

      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant='body2'>{selectedVisible.length} selected</Typography>
        <Components.Actions style={{ justifyContent: 'flex-start' }}>
          <Components.ActionButton type='button' disabled={!hasSelection} onClick={() => add({ type: 'top' })}>
            Add to top
          </Components.ActionButton>
          <Components.ActionButton type='button' disabled={!hasSelection} onClick={() => add({ type: 'bottom' })}>
            Add to bottom
          </Components.ActionButton>
          <TextField
            size='small'
            type='number'
            label='Position'
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            inputProps={{ min: 1, max: feedLength + 1 }}
            sx={{ width: 100 }}
          />
          <Components.ActionButton
            type='button'
            disabled={!hasSelection}
            onClick={() => add({ type: 'position', position: positionNumber })}>
            Insert at position {positionNumber}
          </Components.ActionButton>
        </Components.Actions>
      </Box>
    </Drawer>
  );
};

export default AddShortsPanel;
